import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, StatusBadge } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import { QUOTE_STATUS } from "@/lib/labels";
import { toast } from "sonner";

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const load = useCallback(() => api.get("/quotes").then((r) => setQuotes(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (quotes && focus) {
      const el = document.getElementById(`quote-${focus}`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
    }
  }, [quotes, focus]);

  const update = async (id, patch) => { try { await api.put(`/quotes/${id}`, patch); toast.success("Devis mis à jour"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!quotes) return <Loader />;
  return (
    <div>
      <PageTitle>Devis</PageTitle>
      <div className="space-y-3">
        {quotes.map((q) => {
          const d = drafts[q.id] || { amount: q.amount, admin_notes: q.admin_notes || "" };
          const setD = (patch) => setDrafts((s) => ({ ...s, [q.id]: { ...d, ...patch } }));
          return (
            <Panel key={q.id} id={`quote-${q.id}`} className={`p-4 scroll-mt-24 transition ${focus === q.id ? "ring-2 ring-[#C85A32]" : ""}`} data-testid={`admin-quote-${q.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-num font-bold">{q.quote_number}</span>
                <StatusBadge map={QUOTE_STATUS} value={q.status} />
                {q.urgency === "urgent" && <span className="bg-red-100 text-red-700 text-[11px] font-bold px-2 py-0.5 rounded-full">⚡ URGENT</span>}
                <span className="text-xs text-slate-400 ml-auto">{formatDate(q.created_at)}</span>
              </div>
              <p className="text-sm font-semibold mt-1">{q.product_name} × {q.quantity} — {q.customer_name}</p>
              <p className="text-sm text-slate-500">{q.description}</p>
              <p className="text-xs text-slate-400 mt-1">Zone {q.zone} · {q.address} {q.dimensions && `· Dim: ${q.dimensions}`}</p>
              {q.photos?.length > 0 && <div className="flex gap-2 mt-2">{q.photos.slice(0, 4).map((p, i) => <img key={i} src={p} alt="" className="w-16 h-16 object-cover rounded-lg" />)}</div>}

              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 items-end">
                <label className="text-sm"><span className="block text-xs text-slate-500 mb-1">Montant (FCFA)</span>
                  <input type="number" value={d.amount} onChange={(e) => setD({ amount: Number(e.target.value) })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-32" data-testid={`quote-amount-${q.id}`} /></label>
                <label className="text-sm flex-1 min-w-[180px]"><span className="block text-xs text-slate-500 mb-1">Note au client</span>
                  <input value={d.admin_notes} onChange={(e) => setD({ admin_notes: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" data-testid={`quote-notes-${q.id}`} /></label>
                <button onClick={() => update(q.id, { status: "en_preparation", ...d })} className="bg-slate-200 text-slate-700 text-sm font-bold px-3 py-2 rounded-lg" data-testid={`quote-draft-${q.id}`}>Enregistrer</button>
                <button onClick={() => update(q.id, { status: "envoye", ...d })} className="bg-[#C85A32] text-white text-sm font-bold px-3 py-2 rounded-lg" data-testid={`quote-send-${q.id}`}>Envoyer le devis</button>
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
